
import Vue from 'vue';
import { Component, Emit, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import SponsoredRecommendation from '@/components/sponsored-recommendations/SponsoredRecommendation.vue';
import HorizontalTitleListGraphql from '@/components/timeline/HorizontalTitleListGraphql.vue';

import { TitleListName } from '@/interfaces/responses/title-list';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import type { TitleQuickActionPayload } from '@/helpers/providers/title-actions-provider';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { toastError } from '@/helpers/toast/toast-helper';
import type { SnowplowContext, SnowplowModuleContext } from '@/helpers/tracking/providers';

import type { FilterCollection } from '@/stores/filter.store';
import type { UserSettings } from '@/stores/user.store';

import type { GetNewTitlesQuery, GetNewTitlesQueryVariables } from '@/pages/graphql/queries/GetNewTitles.query';
import { GetNewTitlesDocument } from '@/pages/graphql/queries/GetNewTitles.query';

import {
	BackdropProfile,
	NewDateRangeBucket,
	NewPageType,
	ObjectType,
	Platform,
	SraPlacement,
	TitleFilter,
} from '@/@types/graphql-types';
import { useSponsoredRec } from '@/components/sponsored-recommendations/useSponsoredRec';
import { useMetaStore } from '@/helpers/composables/useStores';

const App = namespace('app');
const Language = namespace('language');
const Filter = namespace('filter');
const User = namespace('user');
const Meta = namespace('meta');

type NewTitle = Pick<NonNullable<GetNewTitlesQuery['newTitles']['edges']>[number], 'node' | 'newOffer'>;

@Component({
	name: 'ProviderTimeline',
	components: {
		PosterGraphql,
		SponsoredRecommendation,
		HorizontalTitleListGraphql,
		ProviderIcon,
	},
	apollo: {
		providerTitlesData: {
			query: GetNewTitlesDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetNewTitlesQueryVariables {
				return this.queryVariables;
			},
			update({ newTitles }) {
				this.isFirstPageLoaded = true;

				if (process.server) {
					// since New.vue is still option-api, we can't listen to watchers in there, that isn't picked up by SSR.
					// that's why we do it for each provider timeline here directly, and flush the visible posters before route changes.
					// only doing process.server check to avoid double counting on client side
					const totalCount = (newTitles.edges || []).length;

					this.setVisiblePosters(this.visiblePosters + totalCount);
				}

				return newTitles;
			},
			skip() {
				return !this.newPageType;
			},
			error() {
				process.client ? this.showErrorToast() : null;
			},
		},
		/**
		 * SponsoredRecommendation special case: SeoUser
		 * we need to send the request from the client in order to have a meaningful SR bidding/tracking
		 * Manually overwrite the titles list
		 */
		sponsoredAdData: {
			query: GetNewTitlesDocument,
			fetchPolicy: 'no-cache',
			prefetch: false,
			variables(): GetNewTitlesQueryVariables {
				return this.queryVariables;
			},
			update({ newTitles }) {
				this.isSeoSRLoaded = true;

				const { sponsoredAd } = newTitles;

				if (newTitles?.edges?.length && sponsoredAd && !sponsoredAd?.holdoutGroup) {
					this.$apollo.getClient().writeQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
						query: GetNewTitlesDocument,
						variables: this.queryVariables,
						data: { newTitles },
					});
				}

				return sponsoredAd;
			},
			skip() {
				return !this.isSeoUser || this.isSeoSRLoaded;
			},
		},
	},
})
export default class ProviderTimeline extends Vue {
	isFirstPageLoaded = false;
	isSeoSRLoaded = false;

	providerTitlesData: GetNewTitlesQuery['newTitles'] = {} as GetNewTitlesQuery['newTitles'];
	sponsoredAdData: GetNewTitlesQuery['newTitles']['sponsoredAd'] =
		{} as GetNewTitlesQuery['newTitles']['sponsoredAd'];

	@Prop({ default: () => [] }) additionalContexts: (SnowplowModuleContext | SnowplowContext)[];
	@Prop({ required: true }) timelineDate: string;
	@Prop({ required: true }) providerId: number;
	@Prop({ required: true }) providerIcon: number;
	@Prop({ default: null }) titleCount: number;
	@Prop({ default: '' }) providerShortName: string;
	@Prop({ default: null }) dayIndex: number;
	@Prop({ default: null }) providerIndex: number;
	@Prop({ default: null }) bucketType: NewDateRangeBucket;

	@App.Getter isSeoUser: boolean;

	@Filter.Getter('new') filtersNew: FilterCollection;
	@Filter.Getter selectedProviders: (isSeoFiltersBar: boolean) => string[];

	@Language.Getter language: string;
	@Language.Getter country: string;

	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;
	@User.State settings: UserSettings;
	@User.Getter isLoggedIn: boolean;

	@Meta.Action setVisiblePosters: (visiblePosters: number) => void;
	@Meta.State visiblePosters: number;

	@Emit('onPosterClick')
	onPosterClick($event: { index: number; title: NewTitle }) {
		return { ...$event, providerId: this.providerId, date: this.timelineDate };
	}

	@Watch('filtersNew')
	changedFilters() {
		this.isFirstPageLoaded = false;
	}

	@Watch('providerTitlesData')
	onProviderTitlesDataChange() {
		if ((this.providerTitlesData?.edges || []).length > 0) {
			this.$emit('passUpTitles', this.providerTitlesData?.edges);
		}
	}

	get sponsoredAd() {
		const { isSeoUser, providerTitlesData, sponsoredAdData } = this;
		return isSeoUser ? sponsoredAdData : providerTitlesData?.sponsoredAd;
	}

	get lazyLoadImages() {
		return !!this.dayIndex || this.providerIndex >= global.IMG_NEW_TIMELINE_ROWS;
	}

	get newPageType() {
		return this.$route?.meta?.newPageType as NewPageType;
	}

	get isPriceDropsTimeline(): boolean {
		return this.newPageType === NewPageType.PriceDrop;
	}

	get imagesLoadCount() {
		return !this.dayIndex && this.providerIndex < global.IMG_NEW_TIMELINE_ROWS ? global.IMG_POSTER_LOAD_COUNT : 0;
	}

	get formattedProviderTitles() {
		const providerTitleEdges = this.providerTitlesData?.edges || [];
		return providerTitleEdges.map(edge => ({
			...edge?.node,
			newOffer: edge.newOffer,
		}));
	}

	get graphqlFilters(): TitleFilter {
		const selectedProviders = this.selectedProviders(!this.isLoggedIn);
		return toGraphqlFilters(this.filtersNew, selectedProviders, this.isHideSeenDislikedTitlesEnabled);
	}

	get queryVariables(): GetNewTitlesQueryVariables {
		const { allowSponsoredRecommendations } = useSponsoredRec({
			pageType: this.newPageType,
			placement: SraPlacement.NewTimeline,
		});

		const variables: GetNewTitlesQueryVariables = {
			date: this.timelineDate,
			filter: {
				...this.graphqlFilters,
				objectTypes: this.filterObjectTypes,
				packages: [this.providerShortName],
			},
			language: this.language,
			country: this.country,
			priceDrops: this.isPriceDropsTimeline,
			platform: Platform.Web,
			pageType: this.newPageType,
			showDateBadge: [NewDateRangeBucket.Next_7Days, NewDateRangeBucket.Next_30Days].includes(this.bucketType),
			availableToPackages: [this.providerShortName],
			backdropProfile: BackdropProfile.S1440,
			allowSponsoredRecommendations: {
				...allowSponsoredRecommendations.value,
				alwaysReturnBidID: false,
			},
		};

		if ([NewPageType.LeavingSoon, NewPageType.Upcoming].includes(this.newPageType) && this.bucketType) {
			variables.bucketType = this.bucketType;
		}

		return variables;
	}

	get isLoading() {
		return this.$apollo.queries.providerTitlesData.loading;
	}

	// We need this mapper due to filter naming on the New Page
	// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
	get filterObjectTypes() {
		return this.graphqlFilters?.objectTypes?.map(objectType =>
			objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
		);
	}

	get titleCountLabel() {
		return this.titleCount === 1
			? this.$t('WEBAPP_TITLE', { NUMBER: this.titleCount })
			: this.$t('WEBAPP_TITLES', { NUMBER: this.titleCount });
	}

	mounted() {
		this.$root.$on('title-quick-action', ({ type, titleObjectId, isTitleInList }: TitleQuickActionPayload) => {
			switch (type) {
				case TitleListName.DISLIKELIST:
					this.isHideSeenDislikedTitlesEnabled && !isTitleInList
						? this.removeTitleFromGraphqlCache(titleObjectId)
						: this.updateListEntryInGraphqlCache(titleObjectId, 'dislikelistEntry', isTitleInList);
					break;
				case TitleListName.SEENLIST:
					if (this.isHideSeenDislikedTitlesEnabled && !isTitleInList) {
						this.removeTitleFromGraphqlCache(titleObjectId);
					}
					break;
				default:
					break;
			}
		});
	}

	newTitlesFromGraphqlCache() {
		return this.$apollo.getClient().readQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
			query: GetNewTitlesDocument,
			variables: this.queryVariables,
		});
	}

	removeTitleFromGraphqlCache(titleObjectId: number) {
		const titles = this.newTitlesFromGraphqlCache();

		let newTitles = [];

		if (titles?.newTitles?.edges?.length) {
			newTitles = titles.newTitles.edges;

			newTitles = newTitles.filter(item => {
				return item.node.__typename === 'Season' && item.node.show
					? item.node.show.objectId !== titleObjectId
					: item.node.objectId !== titleObjectId;
			});

			this.$apollo.getClient().writeQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
				query: GetNewTitlesDocument,
				variables: this.queryVariables,
				data: {
					newTitles: {
						...titles.newTitles,
						totalCount: titles?.newTitles.totalCount - 1,
						edges: newTitles,
					},
				},
			});
		}
	}

	updateListEntryInGraphqlCache(
		titleObjectId: number,
		listName: 'dislikelistEntry' | 'likelistEntry' | 'watchlistEntryV2' | 'seenlistEntry',
		isTitleInList: boolean
	) {
		const titles = this.newTitlesFromGraphqlCache();
		if (titles?.newTitles?.edges) {
			const updatedTitles = titles.newTitles.edges.map(title => {
				if (title.node.objectId === titleObjectId) {
					const shouldClearOppositeListEntry =
						!isTitleInList && (listName === 'dislikelistEntry' || listName === 'likelistEntry');
					const oppositeListEntryName =
						listName === 'dislikelistEntry' ? 'likelistEntry' : 'dislikelistEntry';

					return {
						...title,
						node: {
							...title.node,
							...(shouldClearOppositeListEntry ? { [oppositeListEntryName]: null } : {}),
							[listName]: isTitleInList
								? null
								: { createdAt: new Date().toISOString(), __typename: 'TitleListEntry' },
						},
					};
				}

				return title;
			});

			this.$apollo.getClient().writeQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>({
				query: GetNewTitlesDocument,
				variables: this.queryVariables,
				data: {
					newTitles: {
						...titles.newTitles,
						totalCount: titles?.newTitles.totalCount,
						edges: updatedTitles,
					},
				},
			});
		}
	}

	onLoadNext(complete: Function) {
		complete &&
			this.$apollo.queries.providerTitlesData
				.fetchMore({
					variables: {
						after: this.providerTitlesData.pageInfo?.endCursor,
					},
					updateQuery(previousResult, { fetchMoreResult }) {
						return {
							newTitles: {
								__typename: previousResult.newTitles.__typename,
								totalCount: fetchMoreResult.newTitles.totalCount,
								edges: [...previousResult.newTitles.edges, ...fetchMoreResult.newTitles.edges],
								pageInfo: fetchMoreResult.newTitles.pageInfo,
							},
						};
					},
				})
				.catch(e => {
					captureMessageForSentry(
						'[GraphQL New Page Load More Bucket Titles Issue]:',
						{ error: e, where: 'Component: ProviderTimeline, method: onLoadNext' },
						'error'
					);
				})
				.finally(() => complete());
	}

	showErrorToast() {
		toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}
}
